import styled from 'styled-components';
import { DatasourceTable, newTableTheme } from '@ardoq/table-legacy';
import {
  IntegrationWorkspace,
  TableMapping,
  TableMappingType,
} from '@ardoq/api-types/integrations';
import { WorkspaceName } from '../workspaceName/WorkspaceName';
import { type I18nLocale, t } from '@ardoq/i18n';

const Capitalized = styled.div`
  text-transform: capitalize;
`;

export type DataSourceItem = {
  name: string;
  id?: string;
  rowRepresentation: TableMappingType;
  workspace: IntegrationWorkspace;
  isWorkspaceAvailable: boolean;
};

export const toExpandedRowDataSource = (
  tables: TableMapping[],
  availableWorkspaceIds: ReadonlySet<string>
): DataSourceItem[] =>
  tables.map(table => ({
    name: table.name,
    rowRepresentation: table.rowRepresentation,
    workspace: table.rootWorkspace,
    isWorkspaceAvailable: table.rootWorkspace.id
      ? availableWorkspaceIds.has(table.rootWorkspace.id)
      : false,
  }));

export const ExpandedRowMapping = ({
  dataSource,
  onWorkspaceClick,
  i18nLocale,
}: {
  dataSource: DataSourceItem[];
  onWorkspaceClick: ((workspace: IntegrationWorkspace) => void) | null;
  i18nLocale: I18nLocale;
}) => {
  const hasId = dataSource.every(item => 'id' in item);

  return (
    <DatasourceTable
      dataSource={dataSource}
      components={newTableTheme}
      columns={[
        {
          title: hasId
            ? t(
                i18nLocale,
                'integrations:expandedRowMapping.columns.tablesWithId',
                'Tables (ID)'
              )
            : t(
                i18nLocale,
                'integrations:expandedRowMapping.columns.tables',
                'Tables'
              ),
          valueRender: hasId
            ? (_, { name, id }) => (
                <div>
                  {name} ({id})
                </div>
              )
            : (_, { name }) => <div>{name}</div>,
        },
        {
          dataIndex: 'rowRepresentation',
          title: t(
            i18nLocale,
            'integrations:expandedRowMapping.columns.mappedTo',
            'Mapped to'
          ),
          valueRender: rowRepresentation => (
            <Capitalized>{rowRepresentation}</Capitalized>
          ),
        },
        {
          dataIndex: 'workspace',
          title: t(
            i18nLocale,
            'integrations:expandedRowMapping.columns.workspace',
            'Workspace'
          ),
          valueRender: (
            workspace: IntegrationWorkspace,
            { isWorkspaceAvailable }
          ) => (
            <WorkspaceName
              isWorkspaceAvailable={isWorkspaceAvailable}
              workspace={workspace}
              onWorkspaceClick={onWorkspaceClick}
              i18nLocale={i18nLocale}
            />
          ),
        },
      ]}
    />
  );
};
